import type { ScreeningProgram } from "./hooks"
import { assign, setup } from "xstate"

// Stashed on DISQUALIFIED so NotEligiblePage can show the right copy.
export type ScreeningExclusion = { exclusionMessageId: string; emergency: boolean }

// The member's selections on the "Select your preferences" screen — the inputs to the
// (not-yet-built) availability lookup. `region` is a US state code.
export type BookingPreferences = { appointmentTypeId: string; region: string }

export type BookingFlowInput = { alreadySignedConsent: boolean; program: ScreeningProgram }
export type BookingFlowContext = {
  alreadySignedConsent: boolean
  program: ScreeningProgram
  exclusion: ScreeningExclusion | null
  preferences: BookingPreferences | null
}

export type BookingFlowEvent =
  | { type: "CONTINUE" }
  | { type: "DISQUALIFIED"; exclusion: ScreeningExclusion }
  | { type: "SUBMIT_PREFERENCES"; preferences: BookingPreferences }

export const bookingFlowMachine = setup({
  types: {
    input: {} as BookingFlowInput,
    context: {} as BookingFlowContext,
    events: {} as BookingFlowEvent
  },
  guards: {
    // Skip the consent page when the member has already signed the current required
    // consents — fed in as machine input from getConsentStatus.
    alreadyConsented: ({ context }) => context.alreadySignedConsent,
    // Exit guard, still stubbed off until wired to real data.
    hasNoAvailability: () => false // TODO: derive from the availability lookup
  },
  actions: {
    recordExclusion: assign({
      exclusion: ({ event }) => (event.type === "DISQUALIFIED" ? event.exclusion : null)
    }),
    recordPreferences: assign({
      preferences: ({ event }) => (event.type === "SUBMIT_PREFERENCES" ? event.preferences : null)
    })
  }
}).createMachine({
  id: "bookingFlow",
  // Provider finder launches the flow, so the machine itself starts at Consent.
  initial: "consent",
  context: ({ input }) => ({
    alreadySignedConsent: input.alreadySignedConsent,
    program: input.program,
    exclusion: null,
    preferences: null
  }),
  states: {
    consent: {
      always: [{ target: "healthQuestions", guard: "alreadyConsented" }],
      on: { CONTINUE: "healthQuestions" }
    },

    // Nested so it can grow into multiple (possibly member-data-driven) views without touching
    // the outer states. The page sends DISQUALIFIED on an ineligible result, else CONTINUE.
    healthQuestions: {
      initial: "view",
      states: {
        view: {
          on: {
            CONTINUE: { target: "done" },
            DISQUALIFIED: { target: "#notEligible", actions: "recordExclusion" }
          }
        },
        done: { type: "final" }
      },
      onDone: { target: "seeAvailability" }
    },

    // Nested so the availability journey can grow (preferences -> date -> time) without
    // touching the outer states. Today only the preferences view exists: it records the
    // member's visit-type + location and completes, then the onDone branch decides
    // whether any availability exists (stubbed) before moving on.
    seeAvailability: {
      initial: "preferences",
      states: {
        preferences: {
          on: {
            SUBMIT_PREFERENCES: { target: "done", actions: "recordPreferences" }
          }
        },
        done: { type: "final" }
      },
      onDone: [{ target: "noAvailability", guard: "hasNoAvailability" }, { target: "booked" }]
    },

    booked: { type: "final" },
    notEligible: { id: "notEligible", type: "final" },
    noAvailability: { type: "final" }
  }
})
